import { useForm } from "@tanstack/react-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { Label } from "@/core/components/ui/label";
import { Textarea } from "@/core/components/ui/textarea";
import { notifSilenceError } from "@/core/utils/func/notifApp";
import { useAdminTranslation } from "@/features/admin/i18n/use-admin-translation";
import {
  createRejectApprovalSchema,
  type RejectApprovalFormValues,
} from "@/features/admin/schemas/reject-approval.schema";

/**
 * Dialog de rejet d'une demande d'approbation
 * (`POST /ApprovalRequests/{id}/reject`) — motif OBLIGATOIRE (§6.8).
 * La mutation est portée par la page détail (chaîne d'orchestration).
 */
export function ApprovalRequestRejectDialog({
  open,
  onOpenChange,
  onSubmit,
  pending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (rejectionReason: string) => void;
  pending: boolean;
}) {
  const { t } = useAdminTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : schéma zod traduit reconstruit sur changement de langue (règle projet).
  const schema = useMemo(() => createRejectApprovalSchema(), [i18n.language]);

  const form = useForm({
    defaultValues: { rejectionReason: "" } as RejectApprovalFormValues,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) {
        notifSilenceError(t("requiredFieldError"));
        return;
      }
      onSubmit(parsed.data.rejectionReason.trim());
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t("admin.approvals.decide.rejectTitle")}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.Field name="rejectionReason">
            {(field) => (
              <div className="space-y-1.5">
                <Label htmlFor="rejectionReason">
                  {t("admin.approvals.decide.rejectionReason")}
                </Label>
                <Textarea
                  id="rejectionReason"
                  placeholder={t(
                    "admin.approvals.decide.rejectionReasonPlaceholder"
                  )}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                {field.state.meta.errors.length > 0 && (
                  <p className="text-sm text-destructive">
                    {field.state.meta.errors[0]}
                  </p>
                )}
              </div>
            )}
          </form.Field>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={pending}
            >
              {t("common.cancel")}
            </Button>
            <Button type="submit" variant="destructive" disabled={pending}>
              {pending ? <ButtonSpinner /> : null}
              {t("admin.approvals.decide.reject")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
